import { type OptionalSize, type Size, getIsSizeWithinBounds } from '@lib/size'
import { type SanityFrameBuilderType } from '@frame-builder/data/sanity/queries/types'
import { type SanityFrameBuilderGlass } from '@frame-builder/data/sanity/queries/types/glass'
import { type SanityFrameBuilderProfile } from '@frame-builder/data/sanity/queries/types/profile'
import { type SanityFrameBuilderPassepartoutColor } from '@frame-builder/data/sanity/queries/types/passepartout'
import {
  type SanityFrameBuilderSpacerColor,
  type SanityFrameBuilderSpacerSize,
} from '@frame-builder/data/sanity/queries/types/spacer'
import { type SanityFrameBuilderTexture } from '@frame-builder/data/sanity/queries/types/texture'
import { arrayToSize } from '@frame-builder/lib/size'
import {
  type FrameBuilderConfigurationWithLimits,
  type FrameBuilderFile,
} from '@frame-builder/lib/types'
import {
  getIsProductProfileAvailableForPassepartoutColor,
  getIsProductProfileAvailableForSpacerSize,
  getIsProductProfileAvailableForTexture,
  getIsProductPassepartoutColorValid,
  getIsProductPassepartoutSizeValid,
} from './product-validation'

/**
 * Determines if artwork size is valid.
 */
export const getIsArtworkSizeValid = (
  frameBuilderConfiguration: FrameBuilderConfigurationWithLimits,
  artworkWidth?: number,
  artworkHeight?: number,
) => {
  if (
    typeof artworkWidth === 'undefined' ||
    typeof artworkHeight === 'undefined'
  ) {
    return false
  }

  // Check if width and height are within bounds (global profile minimum or maximum)
  return getIsSizeWithinBounds(
    [artworkWidth, artworkHeight] as const,
    [
      frameBuilderConfiguration.limits.minimumSize,
      frameBuilderConfiguration.limits.minimumSize,
    ],
    [
      frameBuilderConfiguration.limits.maximumWidth,
      frameBuilderConfiguration.limits.maximumHeight,
    ],
  )
}

/**
 * Determines if artwork image is valid.
 */
export const getIsArtworkImageValid = (image?: FrameBuilderFile) => {
  return typeof image !== 'undefined'
}

/**
 * Determines if profile is available for given sizes.
 */
export const getIsProfileAvailableForSize = (
  frameSize: OptionalSize,
  profile?: SanityFrameBuilderProfile,
) => {
  return getIsSizeWithinBounds(
    frameSize,
    arrayToSize([profile?.minimumSize, profile?.minimumSize]),
    arrayToSize([profile?.maximumWidth, profile?.maximumHeight]),
  )
}

/**
 * Determines if texture is valid.
 */
export const getIsTextureValid = (
  frameBuilderConfiguration: FrameBuilderConfigurationWithLimits,
  profile?: SanityFrameBuilderProfile,
  texture?: SanityFrameBuilderTexture,
) => {
  return (
    typeof texture !== 'undefined' &&
    (typeof profile === 'undefined' ||
      getIsProductProfileAvailableForTexture(
        profile,
        texture,
        frameBuilderConfiguration.profileTextures,
      ))
  )
}

/**
 * Determines if profile is valid.
 */
export const getIsProfileValid = (
  frameSize: Size,
  profile?: SanityFrameBuilderProfile,
) => {
  return (
    typeof profile !== 'undefined' &&
    getIsProfileAvailableForSize(frameSize, profile)
  )
}

/**
 * Determines if mount color selection is valid.
 */
export const getIsPassepartoutColorValid = (
  frameBuilderType: SanityFrameBuilderType,
  profile?: SanityFrameBuilderProfile,
  passepartoutColor?: SanityFrameBuilderPassepartoutColor,
) => {
  return (
    getIsProductPassepartoutColorValid(frameBuilderType, passepartoutColor) &&
    (typeof profile === 'undefined' ||
      typeof passepartoutColor === 'undefined' ||
      getIsProductProfileAvailableForPassepartoutColor(
        profile,
        passepartoutColor,
      ))
  )
}

/**
 * Determines if frame size fits within mount color's and profile's maximum dimensions.
 */
export const getIsPassepartoutSizeValid = (
  artworkWidth?: number,
  artworkHeight?: number,
  passepartoutColor?: SanityFrameBuilderPassepartoutColor,
  passepartoutWidth?: number,
  passepartoutHeight?: number,
) => {
  return getIsProductPassepartoutSizeValid(
    artworkWidth,
    artworkHeight,
    passepartoutColor,
    passepartoutWidth,
    passepartoutHeight,
  )
}

/**
 * Determines if mount color can be selected.
 */
export const getIsPassepartoutColorEnabled = (
  passepartoutColor: SanityFrameBuilderPassepartoutColor,
  profile?: SanityFrameBuilderProfile,
) => {
  return (
    typeof profile === 'undefined' ||
    getIsProductProfileAvailableForPassepartoutColor(profile, passepartoutColor)
  )
}

/**
 * Determines if glass selection is valid.
 */
export const getIsGlassValid = (glass?: SanityFrameBuilderGlass) => {
  return typeof glass !== 'undefined'
}

/**
 * Determines if spacer size is available for profile.
 */
export const getIsSpacerSizeValid = (
  profile?: SanityFrameBuilderProfile,
  spacerSize?: SanityFrameBuilderSpacerSize,
) => {
  return (
    typeof profile === 'undefined' ||
    typeof spacerSize === 'undefined' ||
    spacerSize.thickness === 0 ||
    getIsProductProfileAvailableForSpacerSize(profile, spacerSize)
  )
}

/**
 * Determines if spacer color is valid.
 */
export const getIsSpacerColorValid = (
  spacerSize?: SanityFrameBuilderSpacerSize,
  spacerColor?: SanityFrameBuilderSpacerColor,
) => {
  // If spacer size is selected, validate that spacer color is selected as well
  return (
    typeof spacerSize === 'undefined' ||
    spacerSize.thickness === 0 ||
    typeof spacerColor !== 'undefined'
  )
}
